/**
 * Dashboard data cache with stale-while-revalidate (SWR) semantics.
 *
 * Prevents the 7+ API calls that fire every time the user navigates
 * back to the dashboard.  Cached data is served instantly; a background
 * refetch runs when the cache is older than CACHE_TTL_MS.
 *
 * Invalidation triggers:
 *  - activeProject changes  → cache cleared
 *  - invalidateDashboard()  → cache cleared (call after scan completes)
 *  - TTL expires (30 s)     → background refetch on next mount
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from './api';
import { useProject } from './ProjectContext';
import { useSubmissionFocus } from './SubmissionFocusContext';
import { computeSubmissionMetrics, getDocIdsForSubmission, type SubmissionMetrics } from './submissionMetrics';
import { onConsistencyInvalidated } from './consistencyInvalidation';
import type {
  AbbreviationScanResponse,
  CrossReferenceScanResponse,
  DoseUnitScanResponse,
  SubmissionClassification,
  SectionOwnerScanResponse,
  ConsistencyResponse,
  ConsistencyFeatureId,
  RenameConsistencyScanResponse,
} from '@/types/api';
import type { DocSubmissionMap } from './findingClassification';

/** Pull the typed scan response for a feature out of the consistency map. */
function featureValue<T>(
  cons: ConsistencyResponse | null,
  fid: ConsistencyFeatureId,
): T | null {
  const entry = cons?.perFeature?.[fid];
  if (!entry || entry.value == null) return null;
  return entry.value as T;
}

const CACHE_TTL_MS = 30_000; // 30 seconds

export interface DashboardData {
  abbreviations: AbbreviationScanResponse | null;
  crossReferences: CrossReferenceScanResponse | null;
  doseUnit: DoseUnitScanResponse | null;
  /** A2F-815. ``null`` covers both "not scanned" and "gated" (first-sequence
   *  sub) cases; the gated state is distinguishable via
   *  ``renameConsistencyApplicable`` below, which proxies the backend's
   *  per_feature[...].applicable flag. */
  renameConsistency: RenameConsistencyScanResponse | null;
  /** False iff the focused view structurally cannot run a rename-
   *  consistency scan (no submission focused, or focused sub is the
   *  project's first sequence). Drives the "N/A" tile state. */
  renameConsistencyApplicable: boolean;
  classification: SubmissionClassification | null;
  sectionOwners: SectionOwnerScanResponse | null;
  /** Per-submission Signed Off + Consistency metrics (A2F-456). */
  subMetrics: SubmissionMetrics | null;
}

interface DashboardCacheContextValue {
  data: DashboardData | null;
  loading: boolean;
  /** Force a fresh fetch (clears cache first). */
  refresh: () => void;
  /** Clear the cache so the next mount does a real fetch. */
  invalidate: () => void;
}

const DashboardCacheContext = createContext<DashboardCacheContextValue>({
  data: null,
  loading: false,
  refresh: () => {},
  invalidate: () => {},
});

export function useDashboardCache() {
  return useContext(DashboardCacheContext);
}

export function DashboardCacheProvider({ children }: { children: ReactNode }) {
  const { activeProject } = useProject();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const fetchedAtRef = useRef<number>(0);
  const activeProjectIdRef = useRef<string | undefined>(undefined);
  // Track if a fetch is in-flight to avoid double-fetching
  const fetchingRef = useRef(false);

  // Read submission focus from global context (A2F-454)
  const { submissionFocusId, submissions, docSubmissionMap } = useSubmissionFocus();
  // A2F-621: sync the ref synchronously during render (same pattern as
  // submissionsRef / docSubMapRef below). Previously this update lived in
  // the useEffect below, which runs parent-after-child, so DashboardPage's
  // ensureFresh effect could fire fetchData while submissionIdRef.current
  // was still stale — the /consistency call went out without submission_id
  // and the tiles rendered "Not scanned" for the focused sub.
  const submissionIdRef = useRef<string | null>(null);
  submissionIdRef.current = submissionFocusId;
  const submissionsRef = useRef(submissions);
  submissionsRef.current = submissions;
  const docSubMapRef = useRef<DocSubmissionMap | null>(null);
  docSubMapRef.current = docSubmissionMap;

  // Invalidate cache when submission focus changes. The [submissionFocusId]
  // dep array already gives "only fire on change" semantics, so the
  // previous `if (focusId !== ref.current)` guard is redundant once the
  // ref is synced in render.
  useEffect(() => {
    setData(null);
    fetchedAtRef.current = 0;
  }, [submissionFocusId]);

  // Clear cache when project changes
  useEffect(() => {
    if (activeProject?.id !== activeProjectIdRef.current) {
      activeProjectIdRef.current = activeProject?.id;
      setData(null);
      fetchedAtRef.current = 0;
    }
  }, [activeProject?.id]);

  const fetchData = useCallback(
    async (projectId: string) => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      setLoading(true);

      try {
        // A2F-462 follow-up: one aggregate consistency read replaces the
        // 5 individual per-feature GET calls. Server owns the overlay-vs-
        // lifecycle merge.
        const sid = submissionIdRef.current;
        const [consR, clsR, soR] = await Promise.allSettled([
          api.consistency.get(projectId, sid ?? undefined),
          // Classification: only fetch when a specific submission is focused.
          // A2F-489: dropped the silent latest-sub fallback that previously
          // fired when `submissionId === null` — showing one specific
          // submission's classification while the dashboard banner says
          // "All Submissions" was misleading. The Plan card now renders an
          // explicit empty state in All mode (see DashboardPage.tsx).
          (async () => {
            const targetId = submissionIdRef.current;
            if (!targetId) return null;
            const subs = submissionsRef.current;
            const targetSub = subs.find(s => s.id === targetId);
            if (!targetSub) return null;
            return api.submissionTypes.getClassification(
              projectId,
              targetSub.id,
            );
          })(),
          api.sectionOwners.get(projectId),
        ]);

        const cons = consR.status === 'fulfilled' ? consR.value : null;
        const classification = clsR.status === 'fulfilled' ? clsR.value : null;
        const sectionOwners = soR.status === 'fulfilled' ? soR.value : null;

        // Decompose per-feature values back into the old-shape fields so
        // downstream card components (DashboardPage) don't need to know the
        // aggregate endpoint exists. Two features share the abbreviations
        // scan — both pull the same response dict.
        const abbreviations = featureValue<AbbreviationScanResponse>(cons, 'abbreviationsInconsistent');
        const crossReferences = featureValue<CrossReferenceScanResponse>(cons, 'crossReferencesMissing');
        const doseUnit = featureValue<DoseUnitScanResponse>(cons, 'doseUnitInconsistent');
        const renameConsistency = featureValue<RenameConsistencyScanResponse>(cons, 'renameConsistencyInconsistent');
        // Default to true if the field is missing on the response (older
        // backend, or a feature that's never gated) — the only gated
        // feature today is rename_consistency.
        const renameConsistencyApplicable =
          cons?.perFeature?.renameConsistencyInconsistent?.applicable ?? true;

        // Per-submission metrics: % Content Done from the local section-owner
        // scan; consistency from the server aggregate. The dashboard currently
        // only renders Sections + Content Done + Consistency, so passing
        // `null` for docOwners is fine — the unused `documentDone*` fields
        // collapse to "Not assigned" and never reach the render path.
        let subMetrics: SubmissionMetrics | null = null;
        const dsm = docSubMapRef.current;
        if (sid && dsm) {
          const subDocIds = getDocIdsForSubmission(dsm, sid);
          subMetrics = computeSubmissionMetrics(
            subDocIds,
            sectionOwners,
            cons?.aggregate ?? null,
            null,
          );
        }

        const result: DashboardData = {
          abbreviations,
          crossReferences,
          doseUnit,
          renameConsistency,
          renameConsistencyApplicable,
          classification,
          sectionOwners,
          subMetrics,
        };

        setData(result);
        fetchedAtRef.current = Date.now();
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    },
    [],
  );

  // Subscribe to consistency invalidation so any scan completion anywhere
  // in the app triggers a refetch of dashboard data.
  useEffect(() => {
    return onConsistencyInvalidated((invalidatedProjectId) => {
      if (invalidatedProjectId === activeProjectIdRef.current) {
        fetchedAtRef.current = 0;
        const pid = activeProjectIdRef.current;
        if (pid) fetchData(pid);
      }
    });
  }, [fetchData]);

  /**
   * Ensure data is fresh.  Call from DashboardPage on mount.
   *
   * - No cache → blocking fetch (loading = true)
   * - Stale cache → return cached data immediately, background refetch
   * - Fresh cache → no-op
   */
  const ensureFresh = useCallback(() => {
    const pid = activeProject?.id;
    if (!pid) return;

    const age = Date.now() - fetchedAtRef.current;
    if (data && age < CACHE_TTL_MS) return; // fresh
    if (data && age >= CACHE_TTL_MS) {
      // stale — background refetch (don't set loading so UI stays instant)
      fetchData(pid);
      return;
    }
    // no data — blocking fetch
    fetchData(pid);
  }, [activeProject?.id, data, fetchData]);

  const refresh = useCallback(() => {
    setData(null);
    fetchedAtRef.current = 0;
    if (activeProject?.id) {
      fetchData(activeProject.id);
    }
  }, [activeProject?.id, fetchData]);

  const invalidate = useCallback(() => {
    setData(null);
    fetchedAtRef.current = 0;
  }, []);

  return (
    <DashboardCacheContext.Provider
      value={{ data, loading: loading && data === null, refresh, invalidate }}
    >
      {/* Expose ensureFresh to DashboardPage via a hidden context */}
      <EnsureFreshContext.Provider value={ensureFresh}>
        {children}
      </EnsureFreshContext.Provider>
    </DashboardCacheContext.Provider>
  );
}

// Separate context so DashboardPage can trigger the fetch on mount
const EnsureFreshContext = createContext<() => void>(() => {});
export function useEnsureDashboardFresh() {
  return useContext(EnsureFreshContext);
}
